import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { mapApiCurrency, type Currency } from "@/lib/currencies/currenciesStore";
import { refreshStoredAuthUser } from "@/lib/authUser";

export type CompanyStatus = "active" | "inactive";

/** User row with `users.company_id` = this company (`users.role` is the account role, not Spatie RBAC). */
export type CompanyAssignedUser = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  /** Value from `users.role` (e.g. `company-owner`, `employee`). */
  role: string;
  createdAt: string;
  updatedAt: string;
};

/** @deprecated Use `CompanyAssignedUser`. */
export type CompanyOwnerUser = CompanyAssignedUser;

export function formatAccountRoleLabel(role: string): string {
  const t = role.trim();
  if (!t) return "—";
  return t
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export interface CompanyRecord {
  id: ID;
  name: string;
  slug: string;
  domain: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  status: CompanyStatus;
  logo: string | null;
  primaryColor: string | null;
  timezone: string | null;
  currencyId: number | null;
  currency?: any | null;
  createdAt: string;
  updatedAt: string;
  /** Present when company was loaded with detail (show/update/create); empty on list-only cache. */
  companyAssignedUsers: CompanyAssignedUser[];
}

export const INVENTORY_COMPANIES_LS_KEY = "inventory.companies.v1";
const LS_KEY = INVENTORY_COMPANIES_LS_KEY;

type CompanyAssignedUserApi = {
  id?: number | string;
  name?: string;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type CompanyApiRecord = {
  id: number | string;
  name: string;
  slug: string | null;
  domain: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  country?: string | null;
  region?: string | null;
  city?: string | null;
  status: CompanyStatus;
  logo: string | null;
  primary_color?: string | null;
  timezone?: string | null;
  currency_id?: number | string | null;
  currency?: any;
  created_at?: string;
  updated_at?: string;
  company_assigned_users?: CompanyAssignedUserApi[];
  /** Legacy API key (company-owner rows only). */
  company_owner_users?: CompanyAssignedUserApi[];
};

type ListCompaniesApiResponse = {
  data?: CompanyApiRecord[];
  meta?: { page?: number; per_page?: number; total?: number };
};

type UpsertApiResponse = { message?: string; data?: CompanyApiRecord };
type DeleteApiResponse = { message?: string };

function mapAssignedUsersApi(raw: CompanyAssignedUserApi[] | undefined, defaultRoleWhenMissing: string): CompanyAssignedUser[] {
  if (!raw?.length) return [];
  return raw.map((u) => {
    const roleRaw = u.role != null && String(u.role).trim() !== "" ? String(u.role).trim() : defaultRoleWhenMissing;
    return {
      id: String(u.id ?? ""),
      name: (u.name ?? "").trim(),
      email: u.email ?? null,
      phone: u.phone ?? null,
      role: roleRaw,
      createdAt: u.created_at || new Date().toISOString(),
      updatedAt: u.updated_at || u.created_at || new Date().toISOString(),
    };
  });
}

function assignedUsersFromApiPayload(r: CompanyApiRecord): CompanyAssignedUser[] {
  const hasNew = r.company_assigned_users !== undefined && r.company_assigned_users !== null;
  const raw = hasNew ? r.company_assigned_users : r.company_owner_users;
  const defaultRole = hasNew ? "" : "company-owner";
  return mapAssignedUsersApi(raw, defaultRole);
}

function mapApiRecord(r: CompanyApiRecord): CompanyRecord {
  return {
    id: String(r.id),
    name: r.name?.trim() ? r.name : "",
    slug: (r.slug ?? "").trim() || "company",
    domain: r.domain ?? null,
    email: r.email ?? null,
    phone: r.phone ?? null,
    address: r.address ?? null,
    country: r.country ?? null,
    region: r.region ?? null,
    city: r.city ?? null,
    status: r.status === "inactive" ? "inactive" : "active",
    logo: r.logo ?? null,
    primaryColor: r.primary_color ?? null,
    timezone: r.timezone ?? null,
    currencyId: r.currency_id != null && r.currency_id !== "" ? Number(r.currency_id) : null,
    currency: r.currency ? mapApiCurrency(r.currency) : null,
    createdAt: r.created_at || new Date().toISOString(),
    updatedAt: r.updated_at || r.created_at || new Date().toISOString(),
    companyAssignedUsers: assignedUsersFromApiPayload(r),
  };
}

function dedupe(items: CompanyRecord[]): CompanyRecord[] {
  const m = new Map<ID, CompanyRecord>();
  for (const x of items) m.set(x.id, x);
  return [...m.values()];
}

function normalizeCachedCompany(c: CompanyRecord & { companyOwnerUsers?: CompanyAssignedUser[] }): CompanyRecord {
  let users: CompanyAssignedUser[] = Array.isArray(c.companyAssignedUsers) ? c.companyAssignedUsers : [];
  if (!users.length && Array.isArray(c.companyOwnerUsers)) {
    users = c.companyOwnerUsers.map((u) => ({
      id: String(u.id ?? ""),
      name: (typeof u.name === "string" ? u.name : "").trim(),
      email: u.email ?? null,
      phone: u.phone ?? null,
      role: typeof u.role === "string" && u.role.trim() ? u.role : "company-owner",
      createdAt: u.createdAt || new Date().toISOString(),
      updatedAt: u.updatedAt || u.createdAt || new Date().toISOString(),
    }));
  }
  return {
    ...c,
    companyAssignedUsers: users.map((u) => ({
      id: String(u.id ?? ""),
      name: (typeof u.name === "string" ? u.name : "").trim(),
      email: u.email ?? null,
      phone: u.phone ?? null,
      role: typeof u.role === "string" ? u.role : "",
      createdAt: u.createdAt || new Date().toISOString(),
      updatedAt: u.updatedAt || u.createdAt || new Date().toISOString(),
    })),
  };
}

function load(): CompanyRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const p = JSON.parse(raw) as CompanyRecord[];
    return Array.isArray(p) ? dedupe(p.map(normalizeCachedCompany)) : [];
  } catch {
    return [];
  }
}

function save(items: CompanyRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupe(items)));
  window.dispatchEvent(new CustomEvent("inventory_companies_cache_changed"));
}

export function listCompanyRecords(): CompanyRecord[] {
  return load();
}

export function getCompanyById(id: ID): CompanyRecord | undefined {
  return load().find((c) => String(c.id) === String(id));
}

type ShowCompanyApiResponse = { data?: CompanyApiRecord };

export async function fetchCompanyById(id: ID): Promise<CompanyRecord> {
  const res = await api.get<ShowCompanyApiResponse>(`/api/v1/companies/${id}`);
  const row = res.data?.data;
  if (!row) throw new Error("Invalid company response.");
  const record = mapApiRecord(row);
  const items = load();
  const i = items.findIndex((c) => String(c.id) === String(record.id));
  if (i >= 0) items[i] = record;
  else items.push(record);
  save(items);
  return record;
}

export async function syncCompaniesFromApi(): Promise<CompanyRecord[]> {
  const response = await api.get<ListCompaniesApiResponse>("/api/v1/companies", { params: { per_page: 200 } });
  const list = (response.data?.data ?? []).map(mapApiRecord);
  save(list);
  return listCompanyRecords();
}

export type CompanyUpsertPayload = {
  name: string;
  domain?: string | null;
  email?: string | null;
  phone?: string | null;
  contactPerson?: string | null;
  address?: string | null;
  country?: string | null;
  region?: string | null;
  city?: string | null;
  status: CompanyStatus;
  logo?: string | null;
  primaryColor?: string | null;
  timezone?: string | null;
};

function toApiPayload(p: CompanyUpsertPayload) {
  const contactPersonTrimmed = p.contactPerson?.trim();
  return {
    name: p.name.trim(),
    domain: p.domain?.trim() || null,
    email: p.email?.trim() || null,
    phone: p.phone?.trim() || null,
    ...(contactPersonTrimmed ? { contact_person: contactPersonTrimmed } : {}),
    address: p.address?.trim() || null,
    country: p.country?.trim() || null,
    region: p.region?.trim() || null,
    city: p.city?.trim() || null,
    status: p.status,
    logo: p.logo?.trim() || null,
    primary_color: p.primaryColor?.trim() || null,
    timezone: p.timezone?.trim() || null,
  };
}

export type CompanyInitialUserPayload = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  password: string;
};

export type CompanyCreateOptions = {
  /** When set, request is sent as `multipart/form-data` with file field `company_logo` (API stores under `uploads/companies`). */
  logoFile?: File | null;
  /**
   * Clone Company Owner permissions from this tenant when creating the new company (API resolves security for non-super-admins).
   * Omit to let the server use the signed-in user's company_id or global template.
   */
  ownerRoleTemplateCompanyId?: string | null;
  /** When set, created in the same API request/DB transaction as the company (duplicate email fails without creating the company). */
  initialUser?: CompanyInitialUserPayload | null;
};

function initialUserToApi(initial: CompanyInitialUserPayload): Record<string, string> {
  return {
    first_name: initial.firstName.trim(),
    last_name: initial.lastName.trim(),
    phone: initial.phone.trim(),
    email: initial.email.trim().toLowerCase(),
    password: initial.password,
  };
}

export type OwnerRoleTemplatePreview = {
  permissionNames: string[];
  /** `global_template_role` | `global_role` | `company_role` | `all_permissions` */
  source: string;
};

export async function fetchCompanyOwnerRolePermissions(companyId: ID): Promise<OwnerRoleTemplatePreview> {
  const res = await api.get<{ data?: { permission_names?: string[]; source?: string } }>(
    `/api/v1/companies/${companyId}/owner-role-permissions`,
  );
  const d = res.data?.data;
  if (!d || !Array.isArray(d.permission_names) || typeof d.source !== "string") {
    throw new Error("Invalid company owner role permissions response.");
  }
  return { permissionNames: d.permission_names, source: d.source };
}

export async function updateCompanyOwnerRolePermissions(companyId: ID, permissionNames: string[]) {
  const res = await api.put<{ message?: string; data?: { permission_names?: string[]; source?: string } }>(
    `/api/v1/companies/${companyId}/owner-role-permissions`,
    { permission_names: permissionNames },
  );
  const d = res.data?.data;
  if (!d || !Array.isArray(d.permission_names) || typeof d.source !== "string") {
    throw new Error("Invalid update company owner role permissions response.");
  }
  return { message: res.data?.message ?? "Updated successfully.", permissionNames: d.permission_names, source: d.source };
}

/** Default template roles (`company_id` null, `is_template` true) vs company-scoped roles on edit company. */
export type DefaultTemplateRoleMatrixRow = {
  templateRoleId: number;
  name: string;
  guardName: string;
  templatePermissionNames: string[];
  companyRoleId: number | null;
  companyPermissionNames: string[];
};

function mapDefaultTemplateRoleMatrixRow(raw: Record<string, unknown>): DefaultTemplateRoleMatrixRow {
  const tpl = raw.template_permission_names;
  const cmp = raw.company_permission_names;
  return {
    templateRoleId: Number(raw.template_role_id ?? 0),
    name: String(raw.name ?? ""),
    guardName: String(raw.guard_name ?? "web"),
    templatePermissionNames: Array.isArray(tpl) ? tpl.map((p) => String(p)) : [],
    companyRoleId: raw.company_role_id == null || raw.company_role_id === "" ? null : Number(raw.company_role_id),
    companyPermissionNames: Array.isArray(cmp) ? cmp.map((p) => String(p)) : [],
  };
}

export async function fetchCompanyDefaultTemplateRolesMatrix(companyId: ID): Promise<DefaultTemplateRoleMatrixRow[]> {
  const res = await api.get<{ data?: { roles?: unknown[] } }>(
    `/api/v1/companies/${companyId}/default-template-roles-permissions`,
  );
  const roles = res.data?.data?.roles;
  if (!Array.isArray(roles)) throw new Error("Invalid default template roles matrix response.");
  return roles.map((r) => mapDefaultTemplateRoleMatrixRow(r as Record<string, unknown>));
}

export async function updateCompanyDefaultTemplateRolesMatrix(
  companyId: ID,
  roles: Record<string, string[]>,
): Promise<{ message: string; roles: DefaultTemplateRoleMatrixRow[] }> {
  const res = await api.put<{ message?: string; data?: { roles?: unknown[] } }>(
    `/api/v1/companies/${companyId}/default-template-roles-permissions`,
    { roles },
  );
  const list = res.data?.data?.roles;
  if (!Array.isArray(list)) throw new Error("Invalid update default template roles matrix response.");
  return {
    message: res.data?.message ?? "Updated successfully.",
    roles: list.map((r) => mapDefaultTemplateRoleMatrixRow(r as Record<string, unknown>)),
  };
}

/** Company edit Access preview: default “Default Company” template vs company Company Owner perms. */
export type CompanyEditAccessPreview = {
  defaultTemplateRole: { id: number | null; name: string; permissionNames: string[] };
  companyOwner: { roleName: string; permissionNames: string[] };
};

export async function fetchCompanyEditAccessPreview(companyId: ID): Promise<CompanyEditAccessPreview> {
  const res = await api.get<{
    data?: {
      default_template_role?: { id?: number | null; name?: string; permission_names?: string[] };
      company_owner?: { role_name?: string; permission_names?: string[] };
    };
  }>(`/api/v1/companies/${companyId}/access-preview-permissions`);
  const d = res.data?.data;
  const left = d?.default_template_role;
  const right = d?.company_owner;
  if (!left || !right || !Array.isArray(left.permission_names) || !Array.isArray(right.permission_names)) {
    throw new Error("Invalid access preview permissions response.");
  }
  return {
    defaultTemplateRole: {
      id: left.id == null || left.id === "" ? null : Number(left.id),
      name: String(left.name ?? ""),
      permissionNames: left.permission_names.map((p) => String(p)),
    },
    companyOwner: {
      roleName: String(right.role_name ?? "Company Owner"),
      permissionNames: right.permission_names.map((p) => String(p)),
    },
  };
}

export async function fetchOwnerRoleTemplatePermissionsPreview(
  ownerRoleTemplateCompanyId?: string | null,
  options?: { globalTemplate?: boolean },
): Promise<OwnerRoleTemplatePreview> {
  const params: Record<string, string> = {};
  if (options?.globalTemplate) {
    params.global_template = "1";
  } else {
    const tid = normalizeOwnerTemplateCompanyId(ownerRoleTemplateCompanyId ?? null);
    if (tid) {
      params.owner_role_template_company_id = tid;
    }
  }
  const res = await api.get<{ data?: { permission_names?: string[]; source?: string } }>(
    "/api/v1/companies/owner-role-template-permissions",
    { params },
  );
  const d = res.data?.data;
  if (!d || !Array.isArray(d.permission_names) || typeof d.source !== "string") {
    throw new Error("Invalid owner role template preview response.");
  }
  return { permissionNames: d.permission_names, source: d.source };
}

function normalizeOwnerTemplateCompanyId(raw: string | null | undefined): string | null {
  const t = raw?.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 1) return null;
  return String(Math.trunc(n));
}

export async function createCompany(payload: CompanyUpsertPayload, options?: CompanyCreateOptions) {
  const logoFile = options?.logoFile ?? null;
  const templateId = normalizeOwnerTemplateCompanyId(options?.ownerRoleTemplateCompanyId ?? null);
  const body: Record<string, unknown> = { ...toApiPayload(payload) };
  if (templateId) {
    body.owner_role_template_company_id = Number(templateId);
  }
  const iu = options?.initialUser;
  if (iu) {
    body.initial_user = initialUserToApi(iu);
  }

  const res = logoFile
    ? await (() => {
        const form = new FormData();
        for (const [key, value] of Object.entries(body)) {
          if (key === "initial_user" && value && typeof value === "object" && !Array.isArray(value)) {
            const nested = value as Record<string, string>;
            for (const [ik, iv] of Object.entries(nested)) {
              form.append(`initial_user[${ik}]`, iv ?? "");
            }
            continue;
          }
          form.append(key, value === null || value === undefined ? "" : String(value));
        }
        form.append("company_logo", logoFile, logoFile.name?.trim() ? logoFile.name : "company-logo.jpg");
        return api.post<UpsertApiResponse>("/api/v1/companies", form);
      })()
    : await api.post<UpsertApiResponse>("/api/v1/companies", body);

  const row = res.data?.data;
  if (!row) throw new Error("Invalid create company response.");
  const record = mapApiRecord(row);
  const items = load();
  const idx = items.findIndex((x) => x.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.push(record);
  save(items);
  return { record, message: res.data?.message ?? "Company created successfully." };
}

export type CompanyUpdateOptions = {
  logoFile?: File | null;
};

export async function updateCompany(id: ID, payload: CompanyUpsertPayload, options?: CompanyUpdateOptions) {
  const logoFile = options?.logoFile ?? null;
  const body = toApiPayload(payload);

  const res = logoFile
    ? await (() => {
        const form = new FormData();
        // PHP does not reliably parse multipart file uploads on PUT; match employees/brands (POST + spoof).
        form.append("_method", "PUT");
        for (const [key, value] of Object.entries(body)) {
          form.append(key, value === null || value === undefined ? "" : String(value));
        }
        form.append("company_logo", logoFile, logoFile.name?.trim() ? logoFile.name : "company-logo.jpg");
        return api.post<UpsertApiResponse>(`/api/v1/companies/${id}`, form);
      })()
    : await api.put<UpsertApiResponse>(`/api/v1/companies/${id}`, body);

  const row = res.data?.data;
  if (!row) throw new Error("Invalid update company response.");
  const record = mapApiRecord(row);
  const items = load();
  const i = items.findIndex((c) => c.id === id);
  if (i < 0) {
    items.push(record);
  } else {
    items[i] = record;
  }
  save(items);
  void refreshStoredAuthUser();
  return { record, message: res.data?.message ?? "Company updated successfully." };
}

export async function deleteCompany(id: ID) {
  const res = await api.delete<DeleteApiResponse>(`/api/v1/companies/${id}`);
  const items = load();
  const next = items.filter((c) => c.id !== id);
  if (next.length !== items.length) save(next);
  return { success: true, message: res.data?.message ?? "Company deleted successfully." };
}

export function replaceAllCompanies(records: CompanyRecord[]) {
  save(records);
}
